/**
 * Detects when a newer frontend build has actually been deployed and
 * prompts a reload.
 *
 * There are two version signals, and they update at different times:
 *
 *   1. The `x-app-version` response header, stamped by a CloudFront
 *      response-headers policy (see lib/stacks/frontend-stack.ts). This is
 *      infra-level config: it flips to the new build the moment the
 *      `FrontendStack` deploys — which is *before* the `FrontendDeployment`
 *      BucketDeployment finishes uploading the new bundle to S3 and the
 *      CloudFront invalidation propagates. So for a window during every
 *      deploy the header advertises a build whose bundle isn't live yet.
 *
 *   2. The `<meta name="app-build">` tag baked into index.html at vite
 *      build time (see buildMetaPlugin in vite.config.ts). This rides
 *      inside the same S3 object as the bundle, so it can never lead the
 *      deployed code — it changes exactly when the new bundle goes live.
 *
 * Treating the header as authoritative is what made the old "update
 * available" prompt flap: it appeared mid-deploy, and clicking it reloaded
 * the still-old bundle, so the prompt came right back until the upload
 * finished. We therefore use the header only as a cheap "something changed,
 * go verify" trigger (it rides on every API response for free), and gate
 * the prompt on the *confirmed served* build read from index.html. The
 * banner — and the reload it offers — never appear until the new bundle is
 * genuinely live.
 *
 * The header feeds in via `notifyServerVersion(...)`, called from the
 * `handleResponse` chokepoint in `src/services/api.ts`. When it reports a
 * build different from ours we poll index.html across the deploy window
 * until the served build actually changes. A `visibilitychange` listener
 * covers idle tabs that haven't made an API call in a while — when the user
 * comes back we do a one-shot check of the served build.
 *
 * Cross-tab coordination uses a `BroadcastChannel` so the first tab to
 * *confirm* a new served build informs its siblings (banner appears
 * everywhere at once), and the tab whose "Update" button is clicked tells
 * the others to reload so the user doesn't have to click in every tab.
 */

import { parseBuildFromHtml } from './parse-build-meta';

const BOOT_VERSION: string | undefined = import.meta.env.VITE_APP_BUILD;

/**
 * Reload forensics: every PROGRAMMATIC reload writes a sessionStorage
 * breadcrumb just before navigating; `logBootNavigation()` (called once at
 * boot) reads it back alongside the browser's navigation type. Lets us tell
 * apart version-update reloads / peer-tab reloads / manual refreshes when
 * diagnosing "the page suddenly reloaded" reports.
 */
const RELOAD_REASON_KEY = 'optr-reload-reason';

function markReload(reason: string): void {
  try {
    sessionStorage.setItem(
      RELOAD_REASON_KEY,
      JSON.stringify({ reason, at: new Date().toISOString() }),
    );
  } catch {
    // sessionStorage unavailable — diagnostics only, never block the reload
  }
  console.info(`[version-monitor] programmatic reload: ${reason}`);
}

/** Log how this boot came to be (navigation type + any reload breadcrumb). */
export function logBootNavigation(): void {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    const crumbRaw = sessionStorage.getItem(RELOAD_REASON_KEY);
    sessionStorage.removeItem(RELOAD_REASON_KEY);
    const crumb = crumbRaw ? (JSON.parse(crumbRaw) as { reason: string; at: string }) : null;
    console.info('[boot] navigation', {
      type: nav?.type ?? 'unknown', // 'navigate' | 'reload' | 'back_forward' | 'prerender'
      programmaticReload: crumb?.reason ?? null,
      reloadMarkedAt: crumb?.at ?? null,
    });
  } catch {
    // diagnostics only
  }
}

// Refresh the idle-tab probe at most this often. Five minutes is short
// enough that returning users see the prompt promptly, long enough that
// flipping between tabs doesn't generate constant traffic.
const IDLE_PROBE_INTERVAL_MS = 5 * 60 * 1000;

// While the header advertises a build whose bundle isn't live yet, re-check
// the served index.html on this cadence until it actually changes. Bounded
// so a tab that saw a transient/rolled-back header doesn't poll forever.
const CONFIRM_RETRY_MS = 15 * 1000;
const CONFIRM_MAX_ATTEMPTS = 40; // ~10 min — covers upload + invalidation

type Listener = (state: VersionState) => void;

export interface VersionState {
  bootVersion: string | undefined;
  /** The build the server is actually serving, confirmed from index.html. */
  serverVersion: string | null;
  updateAvailable: boolean;
}

let serverVersion: string | null = null;
// Last build advertised by the `x-app-version` header. Only a trigger to
// start confirming — never surfaced to the UI on its own.
let advertisedVersion: string | null = null;
let lastProbeAt = 0;
let confirmAttempts = 0;
let confirmTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

function getState(): VersionState {
  return {
    bootVersion: BOOT_VERSION,
    serverVersion,
    updateAvailable:
      !!BOOT_VERSION &&
      !!serverVersion &&
      serverVersion !== BOOT_VERSION,
  };
}

function emit(): void {
  const snapshot = getState();
  for (const fn of listeners) fn(snapshot);
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getVersionState(): VersionState {
  return getState();
}

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' && BOOT_VERSION
    ? new BroadcastChannel('optr:version')
    : null;

// Suppress re-broadcast when we record a server version in response to a
// peer-tab message, otherwise tabs would echo the version back and forth.
let applyingRemoteVersion = false;

channel?.addEventListener('message', (event) => {
  const data = event.data as { type?: unknown; version?: unknown } | null;
  if (!data || typeof data !== 'object') return;
  if (data.type === 'server-version' && typeof data.version === 'string') {
    // A peer already *confirmed* this served build — trust it directly
    // rather than re-fetching index.html in every tab.
    applyingRemoteVersion = true;
    try {
      recordServedVersion(data.version);
    } finally {
      applyingRemoteVersion = false;
    }
  } else if (data.type === 'reload') {
    markReload('peer-tab-broadcast');
    window.location.reload();
  }
});

/**
 * Record a build confirmed to be actually served. Surfaces the banner when
 * it differs from the build this tab booted with, and tells peer tabs.
 */
function recordServedVersion(version: string): void {
  if (version === serverVersion) return;
  serverVersion = version;
  lastProbeAt = Date.now();
  if (confirmTimer) {
    clearTimeout(confirmTimer);
    confirmTimer = null;
  }
  if (!applyingRemoteVersion && version !== BOOT_VERSION) {
    channel?.postMessage({ type: 'server-version', version });
  }
  emit();
}

/**
 * Read the build the server is actually serving from the
 * `<meta name="app-build">` tag in a freshly-fetched index.html. Returns
 * null when offline, on error, or when the tag is absent (e.g. an old
 * pre-meta bundle is still being served mid-deploy — which correctly reads
 * as "the new bundle isn't live yet").
 */
async function fetchServedBuild(): Promise<string | null> {
  try {
    const res = await fetch('/index.html', { cache: 'no-store' });
    if (!res.ok) return null;
    return parseBuildFromHtml(await res.text());
  } catch {
    return null;
  }
}

/**
 * One-shot: confirm the served build and, if it's newer than ours, record
 * it (which surfaces the banner). Returns true when a newer build was
 * confirmed live.
 */
async function confirmServedBuild(): Promise<boolean> {
  const served = await fetchServedBuild();
  lastProbeAt = Date.now();
  if (served && served !== BOOT_VERSION) {
    recordServedVersion(served);
    return true;
  }
  return false;
}

// Poll the served build across the deploy window until it actually changes
// (then stop), or give up after CONFIRM_MAX_ATTEMPTS so an idle tab that
// saw a transient header isn't left polling forever.
async function pollServedBuild(): Promise<void> {
  confirmTimer = null;
  if (await confirmServedBuild()) return; // live now — done
  confirmAttempts += 1;
  if (confirmAttempts >= CONFIRM_MAX_ATTEMPTS) return;
  confirmTimer = setTimeout(() => void pollServedBuild(), CONFIRM_RETRY_MS);
}

function startConfirming(): void {
  if (getState().updateAvailable) return; // already confirmed
  confirmAttempts = 0;
  if (confirmTimer) {
    clearTimeout(confirmTimer);
    confirmTimer = null;
  }
  void pollServedBuild();
}

/**
 * Called by the API response interceptor with the value of the
 * `x-app-version` response header (or `null` when the header was absent).
 * The header is only a trigger: when it advertises a build different from
 * ours, we start confirming against the actually-served index.html. No-op
 * when the boot version is unknown (e.g. local dev without the
 * vite-injected define), when the header is absent, when it matches the
 * build we booted with, or when we're already chasing that advertised
 * value.
 */
export function notifyServerVersion(version: string | null | undefined): void {
  if (!BOOT_VERSION) return;
  if (!version) return;
  if (version === BOOT_VERSION) return;
  if (version === advertisedVersion) return;
  advertisedVersion = version;
  startConfirming();
}

/**
 * Reload this tab and tell peer tabs to do the same. Wired to the
 * "Update available" CTA so the user only needs to click once. Safe because
 * the CTA only appears once a newer build is confirmed live, so the reload
 * is guaranteed to pick up the new bundle.
 */
export function applyUpdate(): void {
  channel?.postMessage({ type: 'reload' });
  markReload('version-update-cta');
  window.location.reload();
}

if (typeof document !== 'undefined' && BOOT_VERSION) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastProbeAt < IDLE_PROBE_INTERVAL_MS) return;
    void confirmServedBuild();
  });
}
